import React, { FC, memo, useEffect } from 'react'
import { ArrayField, FieldErrors, useWatch } from 'react-hook-form'
import get from 'lodash/get'
import startCase from 'lodash/startCase'
import {
  ControlledHiddenInput,
  ControlledPhoneInput,
  ControlledSelect,
} from 'transfix-form/components'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import { Button } from 'transfix-ui/components/Button'
import { Grid, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipperApplicationContactRoles } from 'types/graphqlTypes'
import { RemoveIcon } from 'transfix-icons'
import { getSelectOptions } from 'utils/getSelectOptions'
import { IEdiContactRoles } from '../../ShipperEdi/components/EdiContactsCard/schemas/EdiContactsSchema'
import { TShipperContactsModalSchema } from './ShipperContactsModalSchema'
import { useShipperContactsModalFormContext } from './useShipperContactsModalForm'

export type TContactKeys = keyof TShipperContactsModalSchema['contacts'][number]

const getErrorMessage = <
  T extends FieldErrors<TShipperContactsModalSchema>,
  K extends TContactKeys
>(
  errors: T,
  index: number,
  keyName: K
): string | undefined => {
  return get(errors, ['contacts', index, keyName, 'message'])
}

interface IShipperContactFieldsProps {
  index: number
  onRemove: () => void
  contact: Partial<ArrayField<TShipperContactsModalSchema['contacts'][number], 'key'>>
  isEdi?: boolean
}

const getFieldName = (index: number, name: TContactKeys) => `contacts.${index}.${name}`

const useShipperContactFieldsStyles = makeStyles(({ spacing }) => ({
  root: {
    borderBottom: `1px solid ${brandColors.coolGray4}`,
    margin: spacing(1.5, 0),
  },
  buttonIconText: {
    display: 'flex',
    gap: spacing(1),
    alignItems: 'center',
  },
}))

export const ShipperContactFields: FC<IShipperContactFieldsProps> = memo(
  ({ contact = {}, index, onRemove, isEdi }) => {
    const classes = useShipperContactFieldsStyles()
    const { control, errors, setValue } = useShipperContactsModalFormContext()

    const idFieldName = getFieldName(index, 'id')
    const roleFieldName = getFieldName(index, 'role')
    const nameFieldName = getFieldName(index, 'name')
    const emailFieldName = getFieldName(index, 'email')
    const secondaryEmailFieldName = getFieldName(index, 'secondaryEmail')
    const phoneFieldName = getFieldName(index, 'phone')
    const phoneExtFieldName = getFieldName(index, 'phoneExtension')
    const notes = getFieldName(index, 'notes')

    const ediContactRoles = Object.values(IEdiContactRoles)
    const shipperContactRoles = Object.values(IShipperApplicationContactRoles)

    const roles = isEdi
      ? ediContactRoles
      : shipperContactRoles.filter(role => !ediContactRoles.includes(role))

    const roleOptions = getSelectOptions({
      options: Object.values(roles),
      mapOption: roleValue => ({
        value: roleValue.toString(),
        label: startCase(roleValue.toString()),
      }),
    })

    const { [`contacts.${index}.role`]: role, [`contacts.${index}.phone`]: phone } = useWatch<{
      [field: string]: any
    }>({
      name: [roleFieldName, phoneFieldName],
    })

    /**
     * Handles clearing phoneExtension value if phone value is cleared
     */
    useEffect(() => {
      if (!phone && phoneExtFieldName) {
        setValue(phoneExtFieldName, '')
      }
    }, [phone, phoneExtFieldName, setValue])

    return (
      <Grid container item spacing={2} className={classes.root} xs={12}>
        <ControlledHiddenInput name={idFieldName} control={control} defaultValue={contact.id} />
        <Grid item xs={6}>
          <ControlledSelect
            name={roleFieldName}
            control={control}
            defaultValue={contact.role}
            SelectProps={{
              label: 'Role',
              validationMessage: getErrorMessage(errors, index, 'role'),
              error: !!getErrorMessage(errors, index, 'role'),
              placeholder: 'Select a Role',
              options: roleOptions,
              required: true,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ControlledTextField
            name={nameFieldName}
            control={control}
            defaultValue={contact.name}
            TextInputProps={{
              label: 'Name',
              validationMessage: getErrorMessage(errors, index, 'name'),
              error: !!getErrorMessage(errors, index, 'name'),
              placeholder: 'Name',
              clearable: true,
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <ControlledTextField
            name={emailFieldName}
            control={control}
            defaultValue={contact.email}
            TextInputProps={{
              label: 'Email',
              validationMessage: getErrorMessage(errors, index, 'email'),
              error: !!getErrorMessage(errors, index, 'email'),
              placeholder: 'Email',
              type: 'email',
              clearable: true,
            }}
          />
        </Grid>
        {role === IShipperApplicationContactRoles.accounting && (
          <Grid item xs={6}>
            <ControlledTextField
              name={secondaryEmailFieldName}
              control={control}
              defaultValue={contact.secondaryEmail}
              TextInputProps={{
                label: 'Secondary Email',
                validationMessage: getErrorMessage(errors, index, 'secondaryEmail'),
                error: !!getErrorMessage(errors, index, 'secondaryEmail'),
                placeholder: 'Secondary Email',
                type: 'email',
                clearable: true,
              }}
            />
          </Grid>
        )}
        <Grid item xs={4}>
          <ControlledPhoneInput
            name={phoneFieldName}
            control={control}
            defaultValue={contact.phone}
            PhoneInputProps={{
              label: 'Phone Number',
              clearable: true,
              validationMessage: getErrorMessage(errors, index, 'phone'),
              invalid: !!getErrorMessage(errors, index, 'phone'),
            }}
          />
        </Grid>
        <Grid item xs={2}>
          <ControlledTextField
            name={phoneExtFieldName}
            control={control}
            defaultValue={contact.phoneExtension}
            TextInputProps={{
              label: 'Ext',
              type: 'number',
              validationMessage: getErrorMessage(errors, index, 'phoneExtension'),
              error: !!getErrorMessage(errors, index, 'phoneExtension'),
              disabled: !phone,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledTextField
            name={notes}
            control={control}
            defaultValue={contact.notes}
            TextInputProps={{
              label: 'Notes',
              validationMessage: getErrorMessage(errors, index, 'notes'),
              error: !!getErrorMessage(errors, index, 'notes'),
              placeholder: 'Contact Notes',
              clearable: true,
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Button color='tertiary' onClick={onRemove}>
            <div className={classes.buttonIconText}>
              <RemoveIcon size='medium' />
              Remove Contact
            </div>
          </Button>
        </Grid>
      </Grid>
    )
  }
)
